import { defaultColors } from '../../Services/contexts/ServicesChartsDataContext';
import { currency } from '../../Util/currency';
import { formatToBRDecimal } from '../../Util/number';

export const useSimpleBars = ({
    values,
    subtitles,
    horizontal = false,
    series = null,
    isMoney = false,
    showDataLabels = true,
    stacked = false,
    customColors = null,
    decimals = 2,
    tooltipTotals = null
}) => {
    const formatValue = (val) => formatToBRDecimal(val, decimals);

    const chartSeries = series ?? [
        {
            name: 'Total',
            data: values || []
        }
    ];

    // Girado, o offsetY vira a folga acima da barra.
    const verticalBarsLabelOffsetY = (rotated) =>
        stacked ? 0 : (rotated ? 5 : -25);

    // Se o maior rotulo nao cabe no espaco de cada barra vertical, giramos
    // todos para manter o grafico uniforme. So da para saber depois de
    // desenhar: o espaco depende da largura do card.
    const fitDataLabels = (chartContext, { config, globals }) => {
        if (horizontal || !showDataLabels || !globals.dataPoints) {
            return;
        }

        const labels = chartContext.el.querySelectorAll('.apexcharts-datalabel');
        // getBBox mede o texto sem a rotacao, entao a medida nao depende da orientacao atual.
        const longestLabel = Math.max(0, ...[...labels].map(label => label.getBBox().width));
        const slotWidth = globals.gridWidth / globals.dataPoints;
        const rotate = longestLabel > slotWidth - 4;
        const rotated = config.plotOptions.bar.dataLabels.orientation === 'vertical';

        if (rotate !== rotated) {
            chartContext.updateOptions({
                plotOptions: { bar: { dataLabels: { orientation: rotate ? 'vertical' : 'horizontal' } } },
                dataLabels: { offsetY: verticalBarsLabelOffsetY(rotate) }
            }, false, false);
        }
    };

    const chartConfig = {
        series: chartSeries,
        options: {
            chart: {
                type: 'bar',
                stacked: stacked,
                toolbar: {
                    show: true
                },
                events: {
                    mounted: fitDataLabels,
                    updated: fitDataLabels
                }
            },
            plotOptions: {
                bar: {
                    borderRadius: 4,
                    horizontal,
                    dataLabels: {
                        position: stacked ? 'center' : 'top',
                        orientation: 'horizontal'
                    }
                }
            },
            legend: {
                position: 'bottom',
                offsetY: 10
            },
            dataLabels: {
                enabled: showDataLabels,
                formatter: function (val) {
                    if (val === 0) {
                        return '';
                    }

                    return formatValue(val);
                },
                style: {
                    colors: stacked ? ['#fff'] : ['#333'] 
                },
                // Ancorado no inicio, o rotulo comeca logo apos a ponta da barra
                // horizontal, qualquer que seja o tamanho do texto.
                textAnchor: horizontal && !stacked ? 'start' : 'middle',
                offsetX: horizontal && !stacked ? 5 : 0,
                offsetY: horizontal ? 0 : verticalBarsLabelOffsetY(false)
            },
            xaxis: {
                categories: subtitles || [],
                labels: {
                    formatter: (val) =>
                        horizontal
                            ? isMoney
                                ? currency.formatToBrCurrency(val)
                                : formatValue(val)
                            : `${val}`
                }
            },
            yaxis: {
                // Folga no fim do eixo para o rotulo da maior barra horizontal
                // caber depois dela; o Apex nao deixa o rotulo sair da area do grafico.
                ...(horizontal && !stacked && showDataLabels && { max: (max) => max * 1.15 }),
                labels: {
                    formatter: (val) =>
                        horizontal
                            ? `${val}`
                            : isMoney
                            ? currency.formatToBrCurrency(val)
                            : formatValue(val)
                }
            },
            fill: {
                opacity: 1,
                colors: customColors || defaultColors
            },
            tooltip: {
                shared: true,
                intersect: false,
                ...(tooltipTotals && {
                    x: {
                        formatter: (val, { dataPointIndex }) =>
                            `${val} · Total: ${formatValue(tooltipTotals[dataPointIndex] ?? 0)}`
                    }
                }),
                y: {
                    formatter: (val) =>
                        isMoney
                            ? currency.formatToBrCurrency(val)
                            : formatValue(val)
                }
            },
            colors: customColors || defaultColors,
            grid: {
                show: false
            }
        }
    };

    return {
        chartConfig
    };
};
